/**
 * <PatientVideoFrame>
 *
 * §2 of <ZoomPanel> (`docs/ui-designs/06-telehealth-and-scribe.md`).
 *
 * 4:3 gradient slate frame for the remote participant video. The Zoom Video
 * SDK lifecycle is owned by `<CallSessionProvider>`; this component claims a
 * DOM container on mount and registers it via
 * `useCallSession().attachRemoteVideoContainer`. The provider then attaches
 * Zoom's `<video-player>` element into that container whenever a
 * `peer-video-state-change` event fires (or when the local user starts video
 * for the first time).
 *
 * Until a stream is attached, a slate avatar fallback fills the frame so the
 * doctor still sees a presence cue.
 *
 * Self-view PiP (bottom-right)
 *   - The provider attaches the doctor's OWN Zoom track into the container
 *     registered via `attachSelfVideoContainer` — Zoom owns the camera, so no
 *     second capture runs. The cam-off fallback shows the provider initials.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCallSession } from '../session'
import type { PatientPresence } from '../session'
import { scribeText } from './scribeText'

// PiP self-view sizing — the doctor can drag the top-left corner to grow
// or shrink the tile. Width is the driving dimension; height is derived
// from `PIP_ASPECT_RATIO` so the tile always looks like a video, never
// stretched. Bounds keep it sensible (tiny enough to not block the
// patient view, large enough to actually see yourself). Persist via
// localStorage so the doctor's preferred size survives reload + remount.
const PIP_ASPECT_RATIO = 16 / 9 // width / height — matches Zoom's fixed 16:9 render (no letterbox)
const PIP_MIN_WIDTH = 60
const PIP_MAX_WIDTH = 280
const PIP_DEFAULT_WIDTH = 76
const PIP_STORAGE_KEY = 'zoom-panel-pip-size'

function widthToHeight(width: number): number {
  return Math.round(width / PIP_ASPECT_RATIO)
}

function readPipWidthFromStorage(): number {
  if (typeof window === 'undefined') return PIP_DEFAULT_WIDTH
  try {
    const raw = window.localStorage.getItem(PIP_STORAGE_KEY)
    if (!raw) return PIP_DEFAULT_WIDTH
    const parsed = JSON.parse(raw) as Partial<{ width: number }>
    const w = Number.isFinite(parsed.width) ? Number(parsed.width) : PIP_DEFAULT_WIDTH
    return Math.min(PIP_MAX_WIDTH, Math.max(PIP_MIN_WIDTH, w))
  } catch {
    return PIP_DEFAULT_WIDTH
  }
}

export type PatientVideoFrameProps = {
  /** Patient initials shown in the avatar fallback (e.g. "CH"). */
  patientInitials: string
  /** Provider initials shown in the cam-off PiP fallback (e.g. "WG"). */
  providerInitials: string
  /** Patient display name rendered in the bottom-left chip. */
  patientName: string
  /** When true, paint a "mic muted" indicator next to the patient name chip. */
  isPatientMuted: boolean
  /** When true, hide the self-view PiP and show the cam-off fallback instead. */
  isVideoOff: boolean
  /**
   * Live patient presence. Drives the primary-tile swap (PD-1067 item 3): the
   * provider self-view is the big primary tile while the patient is absent;
   * when the patient connects, the patient becomes primary and the provider
   * drops to the bottom-right PiP. Also gates the waiting-room banner.
   */
  patientPresence: PatientPresence
  /**
   * Live presence of the staff participant (care coordinator). When
   * `connected`, a small staff tile mounts beside the self-view PiP. Defaults
   * to `not-joined` so 2-party callers render byte-identically.
   */
  staffPresence?: PatientPresence
  /** Staff participant display name for the tile chip + initials fallback. */
  staffName?: string
  /**
   * Optional explicit frame height (px). When omitted, falls back to the
   * natural 4:3 aspect ratio so existing callers keep their layout. The
   * ZoomPanel's vertical drag handle sets this so the doctor can shrink the
   * video and reclaim space for the live transcript stream.
   */
  height?: number
}

export function PatientVideoFrame({
  patientInitials,
  providerInitials,
  patientName,
  isPatientMuted,
  isVideoOff,
  patientPresence,
  staffPresence = 'not-joined',
  staffName = 'Care Coordinator',
  height,
}: PatientVideoFrameProps) {
  const {
    attachRemoteVideoContainer,
    attachStaffVideoContainer,
    attachSelfVideoContainer,
    isJoined,
  } = useCallSession()
  // Provider is the big primary tile until the patient joins; then the patient
  // takes primary and the provider drops to the PiP (Zoom/Meet parity).
  const patientConnected = patientPresence === 'connected'
  const staffConnected = staffPresence === 'connected'
  const staffInitials =
    staffName
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'CC'
  const remoteContainerRef = useRef<HTMLDivElement>(null)
  const selfContainerRef = useRef<HTMLDivElement>(null)
  const staffContainerRef = useRef<HTMLDivElement>(null)

  // PiP self-view resize state — only `width` is tracked; height is
  // derived from `PIP_ASPECT_RATIO` so the tile keeps a 4:3 video shape.
  // Lazy-init from localStorage so a remount preserves the doctor's
  // preferred size.
  const [pipWidth, setPipWidth] = useState<number>(() => readPipWidthFromStorage())
  const pipHeight = widthToHeight(pipWidth)
  const pipDragStateRef = useRef<{
    startX: number
    startY: number
    startWidth: number
  } | null>(null)

  const onPipResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const target = event.currentTarget
      target.setPointerCapture(event.pointerId)
      pipDragStateRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        startWidth: pipWidth,
      }
    },
    [pipWidth],
  )

  const onPipResizePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = pipDragStateRef.current
    if (!drag) return
    // Top-left corner drag from a bottom-right-anchored tile → moving
    // LEFT (negative deltaX) AND UP (negative deltaY) both grow the
    // tile. Take whichever axis's delta is bigger so the drag feels
    // natural on both X and Y motion, then derive height from the
    // aspect ratio so the tile never distorts.
    const deltaFromX = drag.startX - event.clientX
    const deltaFromY = (drag.startY - event.clientY) * PIP_ASPECT_RATIO
    const dominantDelta = Math.abs(deltaFromX) >= Math.abs(deltaFromY) ? deltaFromX : deltaFromY
    const nextWidth = Math.min(
      PIP_MAX_WIDTH,
      Math.max(PIP_MIN_WIDTH, drag.startWidth + dominantDelta),
    )
    setPipWidth(nextWidth)
  }, [])

  const onPipResizePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!pipDragStateRef.current) return
      pipDragStateRef.current = null
      event.currentTarget.releasePointerCapture(event.pointerId)
      try {
        window.localStorage.setItem(PIP_STORAGE_KEY, JSON.stringify({ width: pipWidth }))
      } catch {
        // localStorage may throw in private mode / sandboxed iframes —
        // in-memory state still applies for this session.
      }
    },
    [pipWidth],
  )

  // Register the remote-video host with the provider on mount; release it
  // on unmount so the provider doesn't keep a dangling DOM reference after
  // the panel closes. The provider re-renders any in-flight participant
  // streams against the new container when this fires mid-call.
  useEffect(() => {
    attachRemoteVideoContainer(remoteContainerRef.current)
    return () => {
      attachRemoteVideoContainer(null)
    }
  }, [attachRemoteVideoContainer])

  // Register the PiP self-view host whenever the Zoom self track should be
  // showing. Keyed on visibility — `isVideoOff` unmounts the container for
  // the cam-off fallback, so the provider re-attaches the doctor's video on
  // camera off → on via `attachSelfVideoContainer`.
  useEffect(() => {
    if (!isJoined || isVideoOff) return
    attachSelfVideoContainer(selfContainerRef.current)
    return () => {
      attachSelfVideoContainer(null)
    }
  }, [attachSelfVideoContainer, isJoined, isVideoOff])

  // Register the staff-video host while a coordinator is connected — mirrors
  // the self-view registration. The provider replays the coordinator's stream
  // into it on mount and releases the staff tiles on unmount.
  useEffect(() => {
    if (!isJoined || !staffConnected) return
    attachStaffVideoContainer(staffContainerRef.current)
    return () => {
      attachStaffVideoContainer(null)
    }
  }, [attachStaffVideoContainer, isJoined, staffConnected])

  return (
    <div data-testid="zoom-panel-video-frame" className="relative shrink-0 bg-[#111827]">
      {/* Stable sized frame — owns the 4:3 / drag height and clips the tiles.
          Both video tiles are absolute children whose ROLE (primary vs PiP)
          is a pure CSS toggle so neither video container ever unmounts. */}
      <div
        className={
          height ? 'relative mx-auto overflow-hidden' : 'relative aspect-video overflow-hidden'
        }
        style={{
          background: 'linear-gradient(135deg, #1e293b, #0f172a)',
          // On drag-resize we lock 16:9 (Zoom's render aspect, so the video
          // fills without letterbox): height comes from the drag, width is
          // `height * 16/9`, `maxWidth: 100%` keeps it inside a narrow column,
          // and `mx-auto` centers the box.
          ...(height
            ? {
                height: `${height}px`,
                width: `${Math.round((height * 16) / 9)}px`,
                maxWidth: '100%',
                aspectRatio: '16 / 9',
              }
            : null),
        }}
      >
        {/* PATIENT tile — primary when connected, display:none while absent.
            NEVER unmounted (the remote-attach effect fires once and won't
            re-run), so Zoom keeps painting the patient stream into the same
            container across the swap. */}
        <div
          data-testid="zoom-panel-patient-tile"
          className={patientConnected ? 'absolute inset-0 z-10' : 'hidden'}
        >
          {/* The Zoom SDK appends the patient's `<video-player>` here; the
              initials fallback sits underneath and is painted over when a
              stream is present. */}
          <div
            ref={remoteContainerRef}
            data-testid="zoom-panel-remote-video-container"
            className="pointer-events-none absolute inset-0 z-10"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={`flex h-16 w-16 items-center justify-center rounded-full border-2 border-white/10 ${scribeText.display} text-slate-200`}
              style={{ background: 'linear-gradient(135deg, #475569, #334155)' }}
            >
              {patientInitials}
            </div>
          </div>
        </div>

        {/* PROVIDER self-view — bottom-right PiP when the patient is present,
            promoted to the primary tile while the provider is alone (item 3).
            Position/size is a CSS toggle only; the self container keeps its
            JSX identity so the self stream survives the swap. */}
        <div
          data-testid="zoom-panel-self-view"
          className={
            patientConnected
              ? 'absolute right-2 bottom-2 z-20 overflow-hidden rounded-[7px] border-[1.5px] border-white/[0.12] bg-[#1e293b]'
              : 'absolute inset-0 z-10 overflow-hidden'
          }
          style={patientConnected ? { width: pipWidth, height: pipHeight } : undefined}
        >
          {/* Resize handle only when the provider is the PiP thumbnail — no
              point resizing a full-frame self-view. */}
          {patientConnected && (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize self-view"
              data-testid="zoom-panel-self-view-resize"
              onPointerDown={onPipResizePointerDown}
              onPointerMove={onPipResizePointerMove}
              onPointerUp={onPipResizePointerUp}
              onPointerCancel={onPipResizePointerUp}
              className="absolute top-0 left-0 z-30 flex h-[14px] w-[14px] cursor-nwse-resize items-center justify-center bg-white/[0.18] hover:bg-white/[0.35]"
              style={{ borderBottomRightRadius: 6 }}
            >
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
                aria-hidden="true"
                style={{ pointerEvents: 'none' }}
              >
                <path d="M0 0 L8 8 M0 4 L4 8 M4 0 L8 4" stroke="#fff" strokeWidth="1" />
              </svg>
            </div>
          )}
          {isVideoOff ? (
            <div
              className="flex h-full w-full flex-col items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #1e3a5f, #1e293b)' }}
            >
              <div
                className={`flex items-center justify-center rounded-full text-white ${
                  patientConnected
                    ? `h-6 w-6 ${scribeText.micro} font-bold`
                    : `h-[54px] w-[54px] ${scribeText.display}`
                }`}
                style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}
              >
                {providerInitials}
              </div>
              <span
                className={`font-semibold text-slate-500 ${
                  patientConnected ? scribeText.micro : scribeText.meta
                }`}
              >
                {patientConnected ? 'Cam off' : 'Camera off'}
              </span>
            </div>
          ) : (
            /* The provider appends Zoom's <video-player> for the LOCAL user here
               (renderSelfVideo). Intentionally un-mirrored (no CSS transform,
               mirrorVideo(false)) so the branded VB reads as the patient sees
               it (PD-1271). */
            <div
              ref={selfContainerRef}
              data-testid="zoom-panel-self-video-container"
              className="h-full w-full"
            />
          )}
        </div>

        {/* STAFF (care coordinator) tile — small, bottom-right, sitting left of
            the self-view PiP when the patient is primary. Mounts only while a
            coordinator is connected, so 2-party calls render unchanged. No
            resize grip (that stays exclusive to the self-view). */}
        {staffConnected && (
          <div
            data-testid="zoom-panel-staff-tile"
            className="absolute bottom-2 z-20 overflow-hidden rounded-[7px] border-[1.5px] border-white/[0.12] bg-[#1e293b]"
            style={{
              width: PIP_DEFAULT_WIDTH,
              height: widthToHeight(PIP_DEFAULT_WIDTH),
              right: patientConnected ? pipWidth + 14 : 8,
            }}
          >
            <div
              ref={staffContainerRef}
              data-testid="zoom-panel-staff-video-container"
              className="pointer-events-none absolute inset-0 z-10"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-white ${scribeText.micro} font-bold`}
                style={{ background: 'linear-gradient(135deg, #d97706, #b45309)' }}
              >
                {staffInitials}
              </div>
            </div>
            <div
              data-testid="zoom-panel-staff-name"
              className={`absolute inset-x-0 bottom-0 z-20 truncate bg-black/60 px-1 text-center ${scribeText.micro} text-slate-100`}
            >
              {staffName}
            </div>
          </div>
        )}

        {/* Waiting-room banner — while the patient is not connected. A top
            banner, not a full cover, so the provider's primary self-view
            stays visible behind it (item 2). */}
        {!patientConnected && (
          <div className="absolute inset-x-0 top-3 z-30 flex justify-center px-3">
            <div className="max-w-full rounded-lg bg-black/60 px-3 py-2 text-center">
              <div className={`${scribeText.label} text-slate-100`}>
                {patientPresence === 'disconnected'
                  ? 'Patient disconnected'
                  : `Waiting for ${patientName} to join`}
              </div>
              <div className={`${scribeText.meta} text-slate-400`}>
                {patientPresence === 'disconnected'
                  ? `Waiting for ${patientName} to reconnect…`
                  : 'Patient has not connected to the appointment yet.'}
              </div>
            </div>
          </div>
        )}

        {/* Patient name chip — bottom-left, only while the patient is the
            primary tile (when alone the waiting banner labels the frame). */}
        {patientConnected && (
          <div
            data-testid="zoom-panel-video-name"
            className={`absolute bottom-[6px] left-2 z-20 rounded bg-black/60 px-[7px] py-[2px] ${scribeText.meta} font-semibold text-slate-100`}
          >
            <div className="flex items-center gap-1">
              {isPatientMuted && (
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="2.5"
                  aria-hidden="true"
                >
                  <line x1="1" y1="1" x2="23" y2="23" />
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                </svg>
              )}
              {patientName}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
